import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AudioMutedOutlined, AudioOutlined } from '@ant-design/icons';
import { Button, Flex } from 'antd';
import { MicFeedback } from '@newtontechnologies/libs-browser-mic';
import { useRecording } from '@newtontechnologies/libs-browser-mic/recording';
import { useCallback, useEffect } from 'react';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import { deviceSignal } from '../signalsStore/transcriptionSource';
import MicTranscriptionDots from '../DocumentEditor/MicTranscriptionDots';
import { useRecordingTimer } from './timer';
import styles from './style.module.less';
// NOTE Trial streaming times out after two minutes (with 10 seconds buffer)
const RECORDING_LIMIT_SECONDS = 110;
const MicControls = ({ uploader, onStop, language, }) => {
    const [recording, recordingControl] = useRecording({ chunkSizeInSamples: 16000 });
    const { time, isTimerActive, start, reset, } = useRecordingTimer();
    const { session } = useSession();
    useEffect(() => {
        const handleStarted = async ({ detail }) => {
            void (uploader === null || uploader === void 0 ? void 0 : uploader.setFormat(detail.bytesPerSample, detail.channels));
            await (uploader === null || uploader === void 0 ? void 0 : uploader.connect());
            await (uploader === null || uploader === void 0 ? void 0 : uploader.startTranscription(detail.header, language));
            start();
        };
        const handleAudioChunk = (event) => {
            const { chunk } = event.detail;
            uploader === null || uploader === void 0 ? void 0 : uploader.sendRawAudioChunk(chunk);
        };
        const handleMutedChunk = (event) => {
            const { frames } = event.detail;
            uploader === null || uploader === void 0 ? void 0 : uploader.sendEmptyChunk(frames);
        };
        recordingControl.on('started', handleStarted);
        recordingControl.on('audioChunk', handleAudioChunk);
        recordingControl.on('mutedChunk', handleMutedChunk);
        return () => {
            recordingControl.off('started', handleStarted);
            recordingControl.off('audioChunk', handleAudioChunk);
            recordingControl.off('mutedChunk', handleMutedChunk);
        };
    }, [recordingControl, uploader, start]);
    useEffect(() => {
        if (recording.phase === 'idle' && deviceSignal.value !== undefined) {
            void recordingControl.start({ type: 'microphone', deviceInfo: deviceSignal.value });
        }
        return () => {
            recordingControl.dispose();
        };
    }, []);
    const mute = () => {
        if (recording.phase === 'running') {
            recordingControl.mute('on');
        }
    };
    const unmute = () => {
        if (recording.phase === 'running') {
            recordingControl.mute('off');
        }
    };
    const handleMute = () => {
        if (recording.phase === 'running') {
            if (recording.muted === true) {
                unmute();
            }
            else {
                mute();
            }
        }
    };
    const handleStop = useCallback(() => {
        if (recording.phase === 'running') {
            recordingControl.stop();
        }
        reset();
        onStop();
        recordingControl.dispose();
    }, [recordingControl, recording, reset, onStop]);
    useEffect(() => {
        if (session.login.hasClaim('paidFeatures:enabled')) {
            return;
        }
        if (time.seconds >= RECORDING_LIMIT_SECONDS) {
            handleStop();
        }
    }, [time.seconds, handleStop]);
    return (_jsxs(Flex, { justify: "center", gap: "large", align: "center", children: [_jsx("div", { className: styles.container, children: _jsx(MicFeedback, { recording: recording }) }), _jsx(Button, { className: styles.button, onClick: handleMute, children: recording.phase === 'running' && recording.muted === true ? (_jsx(AudioMutedOutlined, {})) : (_jsx(AudioOutlined, {})) }), _jsx(Button, { className: styles.button, onClick: handleStop, children: _jsx("div", { className: styles.buttonStop }) }), _jsx("div", { className: styles.time, children: isTimerActive === true ? (_jsx("span", { className: styles.timeDigits, children: time.formatted })) : (recording.phase === 'starting' || recording.phase === 'running') ? (_jsxs("span", { className: styles.startingRow, children: [txt('micStartingRecording'), _jsx(MicTranscriptionDots, { compact: true })] })) : (_jsx("span", { className: styles.timeDigits, children: time.formatted })) })] }));
};
export default MicControls;
